import React, { useState, useEffect } from 'react';
import { Users, Plus, Trash2, Edit2, Phone, MapPin, CheckCircle, XCircle } from 'lucide-react';

const CreditorConfig = () => {
  const [creditors, setCreditors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newCreditor, setNewCreditor] = useState({ name: '', phone: '', address: '', status: 'Active', opening_balance: '' });
  const [editingCreditor, setEditingCreditor] = useState(null);
  
  const isAdmin = localStorage.getItem('role') === 'admin';

  useEffect(() => {
    fetchCreditors();
  }, []);

  const fetchCreditors = async () => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/creditors', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setCreditors(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setCreditors([]);
    }
    setIsLoading(false);
  };

  const handleAddCreditor = async (e) => {
    e.preventDefault();
    try {
      await fetch('/api/creditors', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newCreditor)
      });
      setNewCreditor({ name: '', phone: '', address: '', status: 'Active', opening_balance: '' });
      fetchCreditors();
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateCreditor = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    try {
      await fetch(`/api/creditors/${editingCreditor.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(editingCreditor)
      });
      setEditingCreditor(null);
      fetchCreditors();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteCreditor = async (id) => {
    if (!isAdmin || !window.confirm('Are you sure you want to delete this creditor?')) return;
    try {
      await fetch(`/api/creditors/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      fetchCreditors();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex justify-between items-center bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
            <Users className="text-emerald-500" size={32} />
            Creditors Configuration
          </h1>
          <p className="text-slate-500 mt-1 font-medium">Manage your list of creditors</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 sticky top-8">
            <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <Plus className="text-emerald-500" />
              Add New Creditor
            </h2>
            <form onSubmit={handleAddCreditor} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Full Name *</label>
                <input required className="input-field" value={newCreditor.name} onChange={e => setNewCreditor({...newCreditor, name: e.target.value})} placeholder="e.g. John Doe" />
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Phone</label>
                <input className="input-field" value={newCreditor.phone} onChange={e => setNewCreditor({...newCreditor, phone: e.target.value})} placeholder="e.g. 9876543210" />
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Address</label>
                <textarea className="input-field min-h-[100px] resize-y" value={newCreditor.address} onChange={e => setNewCreditor({...newCreditor, address: e.target.value})} placeholder="Company/Home Address..." />
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Opening Balance (₹)</label>
                <input type="number" step="0.01" className="input-field" value={newCreditor.opening_balance} onChange={e => setNewCreditor({...newCreditor, opening_balance: e.target.value})} placeholder="e.g. 5000 (Debit)" />
                <p className="text-xs text-slate-500 mt-1">Enter positive amount if they owe you, negative if you owe them.</p>
              </div>
              <button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 rounded-xl transition-all shadow-md shadow-emerald-200">
                Create Creditor
              </button>
            </form>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="p-6 border-b border-slate-100 bg-slate-50/50">
              <h2 className="text-xl font-bold text-slate-800">Creditor Directory</h2>
            </div>
            
            <div className="divide-y divide-slate-100">
              {creditors.map(c => (
                <div key={c.id} className="p-6 hover:bg-slate-50/80 transition-colors">
                  {editingCreditor?.id === c.id ? (
                    <form onSubmit={handleUpdateCreditor} className="space-y-4 bg-white p-6 rounded-2xl border border-blue-100 shadow-sm">
                      <div className="grid grid-cols-2 gap-4">
                        <input required className="input-field" value={editingCreditor.name} onChange={e => setEditingCreditor({...editingCreditor, name: e.target.value})} placeholder="Name" />
                        <input className="input-field" value={editingCreditor.phone} onChange={e => setEditingCreditor({...editingCreditor, phone: e.target.value})} placeholder="Phone" />
                        <input type="number" step="0.01" className="input-field" value={editingCreditor.opening_balance || ''} onChange={e => setEditingCreditor({...editingCreditor, opening_balance: e.target.value})} placeholder="Opening Balance" />
                      </div>
                      <textarea className="input-field" value={editingCreditor.address} onChange={e => setEditingCreditor({...editingCreditor, address: e.target.value})} placeholder="Address" />
                      <div className="flex justify-between items-center">
                        <select className="input-field w-48" value={editingCreditor.status} onChange={e => setEditingCreditor({...editingCreditor, status: e.target.value})}>
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setEditingCreditor(null)} className="px-4 py-2 text-slate-500 hover:bg-slate-100 rounded-lg font-bold transition-colors">Cancel</button>
                          <button type="submit" className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-bold shadow-md shadow-blue-200 transition-colors">Save Changes</button>
                        </div>
                      </div>
                    </form>
                  ) : (
                    <div className="flex flex-col sm:flex-row justify-between gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <h3 className="font-bold text-lg text-slate-900">{c.name}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${c.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                            {c.status}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                          {c.phone && (
                            <div className="flex items-center gap-1.5 bg-slate-100 px-3 py-1 rounded-lg">
                              <Phone size={14} className="text-slate-400" />
                              <span className="font-medium text-slate-700">{c.phone}</span>
                            </div>
                          )}
                          {c.address && (
                            <div className="flex items-center gap-1.5 bg-slate-100 px-3 py-1 rounded-lg">
                              <MapPin size={14} className="text-slate-400" />
                              <span className="font-medium text-slate-700">{c.address}</span>
                            </div>
                          )}
                          {(c.opening_balance && parseFloat(c.opening_balance) !== 0) ? (
                            <div className="flex items-center gap-1.5 bg-indigo-50 px-3 py-1 rounded-lg border border-indigo-100">
                              <span className="font-semibold text-indigo-700">OB: ₹{parseFloat(c.opening_balance).toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                      
                      {isAdmin && (
                        <div className="flex items-start gap-2 sm:ml-auto">
                          <button onClick={() => setEditingCreditor(c)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-xl transition-colors" title="Edit">
                            <Edit2 size={18} />
                          </button>
                          <button onClick={() => handleDeleteCreditor(c.id)} className="p-2 text-red-400 hover:bg-red-50 hover:text-red-600 rounded-xl transition-colors" title="Delete">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              
              {!isLoading && creditors.length === 0 && (
                <div className="p-12 text-center text-slate-400 font-medium">
                  No creditors configured yet. Add one to get started!
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditorConfig;
